import React, { useCallback, useContext, useRef, useState } from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Input, Field } from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LegacyI18nContext } from '@bingads-webui-react/legacy-i18n-context';
import { withValidation, withError, ValidationGroup } from '@bingads-webui-react/validation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { websiteSchema } from '../validation-schema';
import { websiteValidationErrors } from '../consts';

const getWebsiteInputStyles = () => ({
  websiteInputContainer: {
    width: '100%',
    maxWidth: '527px',
    display: 'flex',
    flexDirection: 'column',
  },
  websiteInputField: {},
});

const ValidatedInput = withError(withValidation(props => (<Input {...props} />)));

export const WebsiteInput = ({
  websiteUrl,
  onWebsiteChange,
  onWebsiteValidate,
  alwaysShowError,
}) => {
  const i18n = useContext(I18nContext);
  const legacyI18n = useContext(LegacyI18nContext);
  const websiteLabelId = 'websiteLabel';
  const classes = useDefaultStyles(getWebsiteInputStyles);
  const [websiteInputError, setWebsiteInputError] = useState('');
  const [showError, setShowError] = useState(false);
  const showErrorTimerRef = useRef(null);
  const errorTimerRef = useRef(null);

  const onChange = useCallback(({ target: { value } }) => {
    onWebsiteChange(value);
    if (showErrorTimerRef.current) {
      clearTimeout(showErrorTimerRef.current);
    }
    showErrorTimerRef.current = setTimeout(() => {
      setShowError(true);
    }, 1500);
  }, [onWebsiteChange]);

  const handleWebsiteError = useCallback((errors) => {
    const errorStrings = _.map(
      errors,
      (oldError, key) => (
        _.has(websiteValidationErrors, key) ?
          i18n.getString(websiteValidationErrors[key]) : oldError)
    );

    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
    }

    if (_.isEmpty(errorStrings)) {
      setWebsiteInputError('');
      return [];
    }

    const customError = _.last(errorStrings);
    errorTimerRef.current = setTimeout(() => {
      setWebsiteInputError(customError);
    }, 1500);
    return [customError];
  }, [i18n]);

  return (
    <div className={classNames('website-input-container', classes.websiteInputContainer)}>
      <ValidationGroup
        validationProps={{
          schema: websiteSchema,
          i18n: legacyI18n,
          data: { website: websiteUrl || '' },
          onValidate: onWebsiteValidate,
        }}
      >
        <div className={classNames('validated-website-input')}>
          <Field
            id={websiteLabelId}
            className={classNames('website-input-field', classes.websiteInputField)}
            label={i18n.getString(_TL_('Website you want customers to visit'))}
            validationMessage={(alwaysShowError || showError) && websiteInputError}
            required
          >
            <ValidatedInput
              id="website-input"
              aria-labelledby={websiteLabelId}
              data-clarity-unmask="true"
              style={{ width: '100%' }}
              validationProps={{
                field: 'website',
                errorProcessor: handleWebsiteError,
                alwaysShowError: alwaysShowError || showError,
                debounce: 1500,
                customErrorRendering: true,
              }}
              onChange={onChange}
              placeholder={i18n.getString((_TL_('https://')))}
            />
          </Field>
        </div>
      </ValidationGroup>
    </div>
  );
};

WebsiteInput.propTypes = {
  websiteUrl: PropTypes.string,
  onWebsiteChange: PropTypes.func.isRequired,
  onWebsiteValidate: PropTypes.func.isRequired,
  alwaysShowError: PropTypes.bool,
};

WebsiteInput.defaultProps = {
  websiteUrl: '',
  alwaysShowError: false,
};
